import classNames from 'classnames';
import { type MouseEvent, memo, type ReactNode, useCallback, useState } from 'react';

import { type Clip, ClipType } from '@air/api/types';
import { SquareArrowOutUpRightIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { clearThumbnailCache } from '@air/utils-clips';

import { AssetGalleryCardShortInfo } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardShortInfo';
import { AssetGalleryCardThumbnailImage } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardThumbnailImage';
import {
  SelectAssetModalAssetInfo,
  type SelectAssetModalAssetItemRenderDragSource,
  type SelectAssetModalAssetItemToggleOptions,
} from '~/components/SelectAssetModal/SelectAssetModalContent/components/SelectAssetModalAssetItem';
import { VideoScrubbableThumbnail } from '~/components/VideoScrubbableThumbnail';
import { VideoStaticThumbnail } from '~/components/VideoStaticThumbnail';
import { Routes } from '~/constants/routes';
import { useVideoScrubbableThumbnail } from '~/hooks/useVideoScrubbableThumbnail';
import { clipSupportsStaticThumbnail } from '~/utils/videoThumbnails';

export type SelectAssetModalGridItemProps = {
  clip: Clip;
  isSelected: boolean;
  isDraftSelected?: boolean;
  // aria-pressed promises a control can be un-pressed; single-select surfaces (the default) must not claim it.
  isMultiSelect?: boolean;
  onToggle: (clip: Clip, options: SelectAssetModalAssetItemToggleOptions) => void;
  onHoverChange?: (clipId: Clip['id'] | null) => void;
  width: number;
  height: number;
  renderDragSource?: SelectAssetModalAssetItemRenderDragSource;
  compact?: boolean;
  registerCardElement?: (clipId: Clip['id'], element: HTMLElement | null) => void;
  showAssetInfo?: boolean;
};

export const SELECT_ASSET_MODAL_GRID_ITEM_TITLE_HEIGHT = 56;

export const SelectAssetModalGridItem = memo(
  ({
    clip,
    isSelected,
    isDraftSelected,
    isMultiSelect = false,
    onToggle,
    onHoverChange,
    width,
    height,
    renderDragSource,
    compact,
    registerCardElement,
    showAssetInfo = true,
  }: SelectAssetModalGridItemProps) => {
    const [isHovering, setIsHovering] = useState(false);

    const cardRef = useCallback(
      (element: HTMLButtonElement | null) => registerCardElement?.(clip.id, element),
      [registerCardElement, clip.id],
    );

    const thumbnailHeight = showAssetInfo ? height - SELECT_ASSET_MODAL_GRID_ITEM_TITLE_HEIGHT : height;

    const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } =
      useVideoScrubbableThumbnail({
        spritesheet: clip.assets.spriteSheet,
        containerDimensions: {
          height: thumbnailHeight,
          width,
        },
      });

    const handleMouseMove = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        setIsHovering(true);
        onHoverChange?.(clip.id);
        onMouseMove(event);
      },
      [onMouseMove, onHoverChange, clip.id],
    );

    // Leaving a card keeps the last hovered clip so the shift range preview survives the gaps between cards; the grid clears it on leaving the whole surface.
    const handleMouseLeave = useCallback(() => {
      setIsHovering(false);
      onMouseLeave();
    }, [onMouseLeave]);

    const handleToggle = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        onToggle(clip, { modifiers: { shiftKey: event.shiftKey, metaKey: event.metaKey, ctrlKey: event.ctrlKey } });
      },
      [onToggle, clip],
    );

    const onOpenInNewTab = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const assetRoute = `${Routes.media.all}/c/${clip.id}`;

        window.open(`${window.location.origin}${assetRoute}`, '_blank', 'noopener,noreferrer');
      },
      [clip],
    );

    const videoSrc = clip.assets.previewVideo;

    const spriteSheetSrc = clip.assets.spriteSheet?.src;
    const isScrubbingVideo = clip.type === ClipType.video && isHovering;

    let thumbnail: ReactNode;

    if (spriteSheetSrc && isScrubbingVideo) {
      thumbnail = (
        <VideoScrubbableThumbnail
          backgroundImageRef={backgroundImageRef}
          indicatorRef={indicatorRef}
          src={spriteSheetSrc}
          style={backgroundImageStyles}
        />
      );
    } else if (clipSupportsStaticThumbnail(clip)) {
      thumbnail = <VideoStaticThumbnail clip={clip} containerDimensions={{ height: thumbnailHeight, width }} />;
    } else {
      thumbnail = (
        <AssetGalleryCardThumbnailImage
          {...clip}
          className="object-cover"
          animatedSrc={clip.type === 'animated' && isHovering && videoSrc ? videoSrc : ''}
          imgixParams={{
            fit: 'clip',
          }}
          src={clearThumbnailCache({
            clipExt: clip.ext,
            clipStatus: clip.status,
            imageSrc: clip.assets.image,
            clipUpdatedAt: clip.updatedAt,
          })}
          width={width}
          height={height}
        />
      );
    }

    const showsSelectedBorder = isSelected || isDraftSelected;
    const clipTitle = clip.title ?? clip.importedName;

    const card = (
      <button
        ref={cardRef}
        type="button"
        onClick={handleToggle}
        aria-pressed={isMultiSelect ? isSelected : undefined}
        className={classNames(
          'group/select-asset-modal-grid-item relative flex flex-col justify-end rounded-16 border-2 text-left ads:hover:bg-hover-subtle hover:bg-gray-3',
          showsSelectedBorder ? 'ads:border-primary border-blue-9' : 'border-transparent',
          isHovering && 'ads:bg-hover-subtle bg-gray-3',
        )}
        data-hovering={isHovering}
        data-selected={isSelected}
        data-testid="SELECT_ASSET_MODAL_GRID_ITEM"
        data-clip-id={clip.id}
        data-clip-title={clipTitle}
        title={clipTitle}
        style={{ height, width }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative flex-1">
          <div className="absolute inset-0.5 flex overflow-hidden rounded-12">{thumbnail}</div>
          <div className="pointer-events-none absolute inset-0.5 z-10">
            <div className="absolute right-2 bottom-2 opacity-100 group-data-[hovering=true]/select-asset-modal-grid-item:opacity-0">
              <AssetGalleryCardShortInfo clip={clip} />
            </div>
            {!showAssetInfo ? (
              <div
                className="absolute inset-0 flex min-h-24 flex-col justify-end overflow-hidden rounded-12 bg-gradient-to-t from-black/85 to-transparent p-2 opacity-0 transition-opacity group-data-[hovering=true]/select-asset-modal-grid-item:opacity-100"
                data-testid="SELECT_ASSET_MODAL_GRID_ITEM_HOVER_INFO"
              >
                <p
                  className="line-clamp-3 break-words font-medium text-12 text-white"
                  data-testid="SELECT_ASSET_MODAL_GRID_ITEM_HOVER_TITLE"
                >
                  {clipTitle}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {showAssetInfo ? <SelectAssetModalAssetInfo clip={clip} compact={compact} /> : null}
        <IconButton
          adsIcon
          icon={SquareArrowOutUpRightIcon}
          label="Open in new tab"
          className="absolute top-3 right-3 opacity-0 transition-opacity group-hover/select-asset-modal-grid-item:opacity-100 group-data-[selected=true]/select-asset-modal-grid-item:opacity-100"
          appearance="filled"
          color="gray"
          size="small"
          data-testid="SELECT_ASSET_MODAL_GRID_ITEM_OPEN_BUTTON"
          onClick={onOpenInNewTab}
        />
      </button>
    );

    return renderDragSource ? renderDragSource({ clip, children: card }) : card;
  },
);

SelectAssetModalGridItem.displayName = 'SelectAssetModalGridItem';
